import React from 'react'
import { CSSObject } from '@emotion/react'

import { Box, DisplayPosition, Image, withCitrusTheme } from '@gousto-internal/citrus-react'

import heroBackgroundDesktop from '../../../../assets/HeroAssets/hero-desktop.jpg'
import heroBackgroundDesktopWebp from '../../../../assets/HeroAssets/hero-desktop.webp'
import heroBackgroundMobileTablet from '../../../../assets/HeroAssets/hero-kitchen.jpg'
import heroBackgroundMobileTabletWebp from '../../../../assets/HeroAssets/hero-kitchen.webp'

const BACKGROUND_ALT =
  'A kitchen counter with a Gousto box overflowing with fresh ingredients and a plated meal'

// Two source crops of the hero: the portrait "kitchen" crop for mobile + tablet (<1024px) and the
// wide landscape crop for desktop (>=1024px). A single <img> carries all the per-breakpoint framing
// via 4-value responsiveStyle arrays [base(<640), 640+, 768+ (tablet), 1024+ (desktop)]; the crop
// itself is chosen by media-gated <source>s (see below) so only ONE asset downloads per viewport —
// <picture> source selection happens at parse time and honours `media`, unlike CSS display (which
// still fetched every crop — B20-1193 / Codex P2 on PR #12254).
//
// Framing per band:
// - Mobile (base, <640px, Figma "MOBILE HERO TWEAKS (POST-LIVE)"): `height`/`top` relative to
//   `HeroRoot`'s own (viewport-relative) height, `width: auto` (preserves aspect ratio, no
//   distortion) — B20-1278, re-derived under B20-1277 (see the styled-component's own comment) to
//   match Figma's own tight box/plate-to-content clearance at each device frame, not an invented
//   "safe" margin.
// - Tablet (640–1023, Figma 183:134/135 for 768–1023): the 768–1023 band uses object-fit cover,
//   centred, with translateY(64px) scale(1.14) to open the ~64px headline↔box gap; the down-shift
//   lifts the image off the top edge (HeroRoot fills). The 640–767 band uses a fixed height/top pair
//   (see below) since `HeroRoot` itself is a fixed height there, not viewport-relative.
// - Desktop (>=1024): object-fit cover, centred.
const HeroBackgroundImage = withCitrusTheme(Image, (theme) => [
  {
    position: 'absolute',
    maxWidth: 'none',
    objectFit: 'cover',
    objectPosition: '50% 50%',
    // Only affects the tablet band, which is the only band (besides mobile, see the `transform`
    // responsiveStyle below) with its own transform-origin.
    transformOrigin: '50% 20%',
    // At the narrow desktop band (1024–1279, e.g. iPad Pro landscape) the hero container is close to
    // square, so object-fit cover crops the desktop asset heavily on the sides and the box sits too
    // far left, under the headline. Lowering objectPosition X shows more of the image's left and
    // pushes the box right, clear of the text. Wider desktop (>=1280) keeps 50% (box already clear).
    '@media (min-width: 1024px) and (max-width: 1279px)': {
      objectPosition: '25% 50%',
    },
  } as CSSObject,
  // Mobile (base, <640px): `height`/`top` are a two-region piecewise fit against Hero height, gated
  // by a discrete `@media (min-height: 712px)` breakpoint — safe again since B20-1277 made
  // `HeroRoot`'s height static `svh` rather than live-tracking `dvh` (a discrete gate previously
  // risked desyncing from a live-tracking height mid-scroll). The "above crossover" region is
  // re-derived under B20-1277 so the plate stays visible above the discount-pills card at real
  // iPhone 16/17 heights, matching Figma's own tight plate/card overlap rather than an invented safe
  // margin — see tasks.md task 10 for the full derivation, rejected alternatives, and the remaining
  // headroom before this needs revisiting. The below-crossover region is untouched.
  {
    width: 'auto',
    left: '50%',
    bottom: 'auto',
    height: 'calc(313.48% - 1370.99px)',
    top: 'calc(44.30% - 153.15px)',
    transform: 'translateX(-50%)',
  } as CSSObject,
  {
    '@media (min-height: 712px)': {
      height: 'calc(307.3091% - 1453.00px)',
      top: 'calc(548.02px - 66.6251%)',
    },
  } as CSSObject,
  // 640–767: `HeroRoot` is a fixed 812px tall in this band (unrelated to the mobile `dvh` work
  // above), so a single fixed height/top pair covers the whole width range — B20-1278. The
  // previous values (`width: 272%`, `height: auto`, i.e. height derived from a width % of a growing
  // container) put the box's front panel well past the discount card at 700px width, since the
  // image scaled with Hero's width but the header/discount positions don't. Re-derived from measured
  // header-bottom (`226px` from Hero's top) and discount-top (`534px`) positions at 700px width, and
  // against the source photo's tallest overflowing produce (a leaf reaching ~19.3% up the image,
  // not the box's own front panel at ~28.5%) — an initial fix that only cleared the panel still let
  // the produce sit under the header text, since the produce extends well above the panel itself.
  // `height: 1010.69px`/`top: 30.77px` let the produce touch the header's bottom edge (`0px` margin
  // — a deliberate, requested "zoomed in slightly" framing, not a clearance target) while keeping
  // the plate's own mid-line clear of the discount card by `20px`. 768+ is unaffected (index 0 is
  // null here, so nothing is emitted for base — the rule above is the only base declaration).
  theme.responsiveStyle([null, 'auto', '100%', '100%'], ['width']) as CSSObject,
  theme.responsiveStyle([null, '1010.69px', '100%', '100%'], ['height']) as CSSObject,
  theme.responsiveStyle([null, '50%', '0px', '0px'], ['left']) as CSSObject,
  theme.responsiveStyle([null, '30.77px', '0px', '0px'], ['top']) as CSSObject,
  // index 0 is null (not 'none') — the base/height-gated rules above already set their own
  // `transform` (needed to position a variable-width image); this must not clobber them. 640–767 now
  // needs `translateX(-50%)` too (centring, to pair with `left: 50%` above) rather than `none`.
  theme.responsiveStyle(
    [null, 'translateX(-50%)', 'translateY(64px) scale(1.14)', 'none'],
    ['transform'],
  ) as CSSObject,
  // Small-phone (<=420px) sub-tuning is no longer needed — object-fit: cover handles that range
  // natively. Removed rather than kept as dead code (see tasks.md task 3.2).
])

// Desktop (>=1024): horizontal, dark on the left behind the left-aligned content. Mobile (base +
// 640–767) + tablet (768–1023): vertical, dark top + bottom behind the top-pinned header and
// bottom-pinned offer. The 640–767 band keeps the mobile gradient so it stays consistent with the
// mobile crop + headline in that band (tablet framing starts at 768).
const MOBILE_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, rgba(7,9,11,0) 32.21%, rgba(7,9,11,0) 75%, rgba(0,0,0,0.65) 100%)'
const TABLET_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.65) 7.33%, rgba(7,9,11,0) 45.32%, rgba(7,9,11,0) 80.84%, rgba(0,0,0,0.65) 100%)'
const DESKTOP_GRADIENT = 'linear-gradient(to right, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 61.8%)'
const GradientOverlay = withCitrusTheme(Box, (theme) =>
  theme.responsiveStyle(
    [MOBILE_GRADIENT, MOBILE_GRADIENT, TABLET_GRADIENT, DESKTOP_GRADIENT],
    ['background'],
  ),
)

// The hero background: a single <picture> whose media-gated <source>s serve the desktop crop at
// >=1024px and the kitchen crop below it, so only the matched crop is fetched. Both crops offer a
// ~50%-smaller WebP with a JPEG fallback. The <img>'s own `src` is the kitchen JPEG — the <1024px
// default — because the fallback `src` also downloads, so it must be the small-viewport asset (gating
// only <source> is not enough). display:contents keeps the <picture> from adding a layout box.
export function CWCHeroBackground() {
  return (
    <>
      <picture style={{ display: 'contents' }}>
        <source media="(min-width: 1024px)" type="image/webp" srcSet={heroBackgroundDesktopWebp} />
        <source media="(min-width: 1024px)" srcSet={heroBackgroundDesktop} />
        <source type="image/webp" srcSet={heroBackgroundMobileTabletWebp} />
        <HeroBackgroundImage src={heroBackgroundMobileTablet} alt={BACKGROUND_ALT} />
      </picture>
      <GradientOverlay
        position={DisplayPosition.Absolute}
        top="0"
        left="0"
        width="100%"
        height="100%"
      />
    </>
  )
}
